import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withCtx as _withCtx, createSlots as _createSlots, createVNode as _createVNode, renderSlot as _renderSlot, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = ["innerHTML"]

import { App } from "@src-capacitor/@capacitor/app"
import { PluginListenerHandle } from "@src-capacitor/@capacitor/core"
import { ComponentConstructor, useDialogPluginComponent } from "quasar"
import SfxTopNavigation from "src/components/navigation/SfxTopNavigation.vue"
import { useTopNavigationStore } from "src/stores/topNavigationStore"
import { ContextMenuItem } from "src/util/classes/contextMenuItem"
import { ContextSource } from "src/util/enums/contextSource"
import { ContextAction } from "src/util/enums/entityType"
import { NavigationAction } from "src/util/enums/topNavigation"
import { showFeedback } from "src/util/functions/dialogs"
import { VueHTMLClass } from "src/util/types/shims"
import { computed, onMounted, onUnmounted, PropType, ref } from "vue"

/*
 * NOTE FOR FUTURE:
 * If we want to be able to return a value from an overlay page, as we can do with dialogs,
 * this can be done when using the slot to provide content in another template.
 * However, we may want to catch a special event from the content component for function
 * initiation and to keep page content in its own components.
 */

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxOverlay',
  props: {
  modelValue: {
    type: Boolean,
    default: undefined,
  },

  title: {
    required: true,
    type: String,
  },

  titleClass: {
    type: String,
  },

  subtitle: {
    type: String,
  },

  subtitleHtml: {
    type: Boolean,
    default: false,
  },

  contextSource: {
    type: String as PropType<ContextSource>,
    default: ContextSource.GENERIC_OVERLAY,
  },

  quickAction: {
    type: Number as PropType<ContextAction>,
  },

  menuContent: {
    type: Array as PropType<Array<ContextAction>>,
  },

  disabledMenuContent: {
    type: Array as PropType<Array<ContextAction>>,
  },

  disableQuickAction: {
    type: Boolean,
    default: false,
  },

  disableMenu: {
    type: Boolean,
    default: false,
  },

  hideContextMenu: {
    type: Boolean,
    default: false,
  },

  hideContextBar: {
    type: Boolean,
    default: false,
  },

  disableNotifications: {
    type: Boolean,
    default: true,
  },

  hideNotifications: Boolean,

  hideSeparator: Boolean,

  pageClasses: {
    type: [Array, Object] as PropType<VueHTMLClass>,
    default: undefined,
  },

  contentComponent: {
    type: Object as PropType<ComponentConstructor>,
  },

  contentComponentProps: {
    type: Object as PropType<unknown>,
  },
},
  emits: [...useDialogPluginComponent.emits, "update:modelValue"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

const topNavigationStore = useTopNavigationStore()

// For passing through the v-model
const model = computed({
  get() {
    return props.modelValue
  },

  set(value) {
    return emit("update:modelValue", value)
  },
})

const { dialogRef, onDialogCancel, onDialogOK, onDialogHide } = useDialogPluginComponent()

const topNavigationContainer = ref<HTMLDivElement | null>(null)

const contentStyle = computed(() => {
  const topNavigationHeight = topNavigationContainer.value?.offsetHeight || 0
  return { height: `calc(100% - ${topNavigationHeight}px)` }
})

const onTop = ref(true)
const contentClasses = computed<Record<string, boolean>>(() => {
  const menuShadowPatch: Record<string, boolean> = { "inset-shadow": onTop.value }

  const pageClassPatch: Record<string, boolean> = {}
  if (Array.isArray(props.pageClasses)) {
    for (const pageClass of props.pageClasses) {
      pageClassPatch[pageClass] = true
    }
  } else {
    Object.assign(pageClassPatch, props.pageClasses)
  }

  return Object.assign({}, menuShadowPatch, pageClassPatch)
})

let androidBackButtonListener: PluginListenerHandle | undefined
const htmlAllowed = computed(() => topNavigationStore.htmlAllowed)

//endregion

//region Lifecycle

onMounted(async () => await addBackClickListener())

onUnmounted(async () => {
  await removeBackClickListener()
})

//endregion

//region Functions

async function addBackClickListener() {
  if (process.env.MODE === "capacitor") {
    androidBackButtonListener = await App.addListener("backButton", async () => {
      await androidBackButtonListener?.remove()
      onDialogCancel()
    })
  }
}

async function removeBackClickListener() {
  if (process.env.MODE === "capacitor") {
    await androidBackButtonListener?.remove()
  }
}

/**
 *
 * @param info Get complete typing from node_modules/quasar/dist/types/index.d.ts
 */
function onScroll(info: { verticalPosition: number }) {
  onTop.value = info.verticalPosition > 20
}

function handleContextSelect(contextMenuItem: ContextMenuItem) {
  return contextMenuItem.action === ContextAction.FEEDBACK
    ? // TODO: We should handle the HTML case somehow
      showFeedback({
        context: props.subtitle,
        designation: props.title,
      })
    : topNavigationStore.setOverlayContextAction(contextMenuItem, props.contextSource)
}

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_scroll_area = _resolveComponent("q-scroll-area")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    modelValue: model.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((model).value = $event)),
    ref_key: "dialogRef",
    ref: dialogRef,
    onHide: _unref(onDialogHide),
    "transition-show": "slide-up",
    "transition-hide": "slide-down",
    maximized: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin" }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            ref_key: "topNavigationContainer",
            ref: topNavigationContainer
          }, [
            _createVNode(SfxTopNavigation, {
              "navigation-action": _unref(NavigationAction).CLOSE,
              "quick-action": props.quickAction,
              "menu-content": props.menuContent,
              "disabled-menu-content": props.disabledMenuContent,
              "disable-quick-action": props.disableQuickAction,
              "disable-menu": props.disableMenu,
              "hide-context-menu": props.hideContextMenu,
              "hide-context-bar": props.hideContextBar,
              "disable-notifications": props.disableNotifications,
              "hide-notifications": props.hideNotifications,
              "hide-separator": props.hideSeparator,
              onContextSelect: handleContextSelect,
              onClose: _unref(onDialogCancel),
              class: "shadow"
            }, _createSlots({ _: 2 }, [
              (props.title)
                ? {
                    name: "title",
                    fn: _withCtx(() => [
                      _createElementVNode("span", {
                        class: _normalizeClass(props.titleClass)
                      }, [
                        (htmlAllowed.value)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 0,
                              innerHTML: props.title
                            }, null, 8, _hoisted_1))
                          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              _createTextVNode(_toDisplayString(props.title), 1)
                            ], 64))
                      ], 2)
                    ]),
                    key: "0"
                  }
                : undefined,
              (props.subtitle)
                ? {
                    name: "subtitle",
                    fn: _withCtx(() => [
                      (props.subtitleHtml)
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 0,
                            innerHTML: props.subtitle
                          }, null, 8, _hoisted_2))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createTextVNode(_toDisplayString(props.subtitle), 1)
                          ], 64))
                    ]),
                    key: "1"
                  }
                : undefined
            ]), 1032, ["navigation-action", "quick-action", "menu-content", "disabled-menu-content", "disable-quick-action", "disable-menu", "hide-context-menu", "hide-context-bar", "disable-notifications", "hide-notifications", "hide-separator", "onClose"])
          ], 512),
          _createVNode(_component_q_scroll_area, {
            class: _normalizeClass(["full-width scrollbar-area", contentClasses.value]),
            onScroll: onScroll,
            style: _normalizeStyle(contentStyle.value)
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_card_section, { class: "full-height" }, {
                default: _withCtx(() => [
                  (_ctx.$slots.default)
                    ? _renderSlot(_ctx.$slots, "default", { key: 0 })
                    : _createCommentVNode("", true),
                  (props.contentComponent)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(props.contentComponent), _mergeProps({ key: 1 }, props.contentComponentProps, {
                        onDialogOk: _cache[0] || (_cache[0] = ($event: any) => (_unref(onDialogOK)($event))),
                        onDialogHide: _cache[1] || (_cache[1] = ($event: any) => (_unref(onDialogHide)()))
                      }), null, 16))
                    : _createCommentVNode("", true)
                ]),
                _: 3
              })
            ]),
            _: 3
          }, 8, ["class", "style"])
        ]),
        _: 3
      })
    ]),
    _: 3
  }, 8, ["modelValue", "onHide"]))
}
}

})